import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from 'reactn';
import { LOCATIONS } from '../../../../../constants';
import { EVENTS } from '../../../../services/constants';
import { eventEmitter } from '../../../../services/event-emitter-service';
import { fetchReentryPlans, MAX_REENTRY_PLANS_PAGE_SIZE } from '../../api/reentryPlansApi';
import {
  buildReentryPlanDetailsPath,
  CASE_PLAN_CREATE_PATH,
  CASE_PLAN_CREATE_PERMISSIONS,
  CASE_PLAN_PREVIEW_RELEASE_PERMISSIONS,
  CASE_PLAN_REENTRY_ALL_PLANS_VIEW_PERMISSIONS,
  REENTRY_PLANS_DEFAULT_PAGE_SIZE,
  REENTRY_PLANS_LIST_SCOPES,
} from '../../constants/reentry-planning.constants';
import {
  canShowAllPlansViewPlan,
  hasCasePlanAssignedDetailAccess,
  createViewPlanClickHandler,
  getLocationFilterOptions,
  mapLocationFilterToLocationIds,
  resolvePlanTypesFromLocations,
  shouldShowCreatePlanButton,
  REENTRY_PLANS_LOCATION_COLUMN,
} from '../../utils/reentry-planning.util';
import {
  resolveCanViewPlanColumn,
  resolveCanViewPlanRow,
} from '../../utils/reentry-plans-scope.util';
import { isAnyPermitted } from '../../utils/case-plan-permission.util';
import { ViewPlanLink } from './ReentryPlansTable.styles';

const noopViewPlanHandler = () => {};

const denyAllPlanRows = () => false;

const EMPTY_ENTITLED_PLAN_TYPES = [];

const LEARNER_CELL_FILTER_MAP = {
  username: 'learnerUsername',
  firstName: 'learnerFirstName',
  lastName: 'learnerLastName',
  docId: 'docId',
};

const SORT_COLUMN_MAP = {
  LearnerCell: 'learnerName',
  learnerName: 'learnerName',
  status: 'status',
  updatedAt: 'updatedAt',
};

const ALLOWED_SORT_FIELDS = new Set(['updatedAt', 'learnerName', 'status']);

export const normalizeReentryPlanFilters = (filters) => {
  if (!Array.isArray(filters)) {
    return filters && typeof filters === 'object' ? filters : {};
  }

  return filters.reduce((acc, filter) => {
    if (!filter?.columnName || filter.columnName === REENTRY_PLANS_LOCATION_COLUMN) {
      return acc;
    }

    let value = filter.value;
    if (Array.isArray(value)) {
      value = value.join(',');
    }

    if (value != null && value !== '') {
      const paramName = LEARNER_CELL_FILTER_MAP[filter.preDefinedPropName] || filter.columnName;
      acc[paramName] = value;
    }
    return acc;
  }, {});
};

export const mapSortColumnToApi = (columnName) => {
  const mapped = SORT_COLUMN_MAP[columnName] || columnName;
  return ALLOWED_SORT_FIELDS.has(mapped) ? mapped : 'updatedAt';
};

const renderViewPlanAction = (onViewPlan, plan) => {
  const handleViewPlan = createViewPlanClickHandler(onViewPlan, plan);

  return (
    <ViewPlanLink
      variant="body1"
      role="button"
      tabIndex={0}
      onClick={handleViewPlan}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleViewPlan();
        }
      }}
    >
      View Plan
    </ViewPlanLink>
  );
};

/**
 * Maps case-plans list results to DataGrid rows.
 *
 * @param {Array<object>} data - Raw list results.
 * @param {Function} onViewPlan - Navigation handler for the View Plan action.
 * @param {(plan: object) => boolean} canViewPlanRow - Per-row View Plan predicate; rows that
 *   fail it get a null action so the cell renders empty rather than disabled.
 */
export const mapResponseToGridRows = (
  data = [],
  onViewPlan = noopViewPlanHandler,
  canViewPlanRow = denyAllPlanRows,
) =>
  data.map((plan) => ({
    rowData: {
      id: plan.planId,
      learnerFirstName: plan.learnerFirstName || '',
      learnerLastName: plan.learnerLastName || '',
      learnerUsername: plan.learnerUsername || '',
      docId: plan.docId || '',
      learnerPersonId: plan.learnerPersonId,
      planId: plan.planId,
      status: plan.status || '',
      updatedAt: plan.updatedAt || '',
      action: canViewPlanRow(plan) ? () => renderViewPlanAction(onViewPlan, plan) : null,
    },
    preDefineComponentProps: {
      LearnerCell: {
        username: plan.learnerUsername || '',
        firstName: plan.learnerFirstName || '',
        lastName: plan.learnerLastName || '',
        docId: plan.docId || '',
      },
    },
  }));

export const useReentryPlansTableHook = ({
  entitledPlanTypes = EMPTY_ENTITLED_PLAN_TYPES,
  listScope = REENTRY_PLANS_LIST_SCOPES.ALL,
  refreshSignal,
} = {}) => {
  const [global] = useGlobal();
  const navigate = useNavigate();
  const { cbacConfigFlag, permissions, permissionIds, isSuperAdmin } = global;
  const [locationsRevision, setLocationsRevision] = useState(0);

  // Assigned-detail unlocks My Plans View Plan. Preview-release stays on All Plans.
  const canViewAssignedPlans = useMemo(
    () => hasCasePlanAssignedDetailAccess(permissions, permissionIds),
    [permissionIds, permissions],
  );

  const hasPreviewReleasePermission = isAnyPermitted(
    cbacConfigFlag,
    permissions,
    CASE_PLAN_PREVIEW_RELEASE_PERMISSIONS,
    permissionIds,
  );

  // View-any-detail only. Assigned-detail, edit, and create must not unlock All Plans View Plan.
  const hasReentryPermission = isAnyPermitted(
    cbacConfigFlag,
    permissions,
    CASE_PLAN_REENTRY_ALL_PLANS_VIEW_PERMISSIONS,
    permissionIds,
  );

  // All Plans View Plan uses the same LOCATIONS LFA source as the side-nav Reentry item.
  const canViewAllPlansDetail = useMemo(
    () =>
      canShowAllPlansViewPlan({
        hasPreviewReleasePermission,
        hasReentryPermission,
        locations: LOCATIONS,
      }),
    // LOCATIONS is module-level; `locationsRevision` bumps when it is populated (LOCATIONS_FETCHED).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [hasPreviewReleasePermission, hasReentryPermission, locationsRevision],
  );

  const canViewPlanRow = useCallback(
    () =>
      resolveCanViewPlanRow({
        canViewAssignedPlans,
        canViewAllPlans: canViewAllPlansDetail,
        listScope,
      }),
    [canViewAllPlansDetail, canViewAssignedPlans, listScope],
  );

  // CBAC create permission (canonical or legacy) + MVP LFA entitlement, both required.
  const canCreatePlan = useMemo(
    () =>
      shouldShowCreatePlanButton(entitledPlanTypes) &&
      isAnyPermitted(cbacConfigFlag, permissions, CASE_PLAN_CREATE_PERMISSIONS, permissionIds),
    [entitledPlanTypes, cbacConfigFlag, permissionIds, permissions],
  );
  const [plans, setPlans] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(REENTRY_PLANS_DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState([]);
  const [renderedScope, setRenderedScope] = useState(listScope);

  // A tab switch restarts paging while filters and sort intentionally persist across scopes.
  // Adjusting during render (instead of in an effect) avoids a fetch for the stale page.
  if (renderedScope !== listScope) {
    setRenderedScope(listScope);
    setPage(1);
  }

  const locationFilterOptions = useMemo(
    () => getLocationFilterOptions(LOCATIONS),
    // LOCATIONS is module-level; `locationsRevision` bumps when it is populated (LOCATIONS_FETCHED).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationsRevision],
  );

  const handleViewPlan = useCallback(
    (plan) => {
      if (plan?.planId) {
        navigate(buildReentryPlanDetailsPath(plan.planId));
      }
    },
    [navigate],
  );

  const loadPlans = useCallback(async () => {
    const locationIds = mapLocationFilterToLocationIds(filters, LOCATIONS);
    let planTypes;

    if (!isSuperAdmin) {
      const scopedLocationIds = locationIds
        ? new Set(locationIds.split(',').map(Number))
        : undefined;
      planTypes = resolvePlanTypesFromLocations(LOCATIONS, scopedLocationIds);
    }

    setIsLoading(true);
    try {
      const filterValues = normalizeReentryPlanFilters(filters);
      const response = await fetchReentryPlans({
        ...(locationIds ? { locationIds } : {}),
        ...(Array.isArray(planTypes) && planTypes.length > 0 ? { planTypes } : {}),
        page,
        pageSize,
        scope: listScope,
        sortBy,
        sortOrder: sortDirection,
        filters: filterValues,
      });

      const planData = Array.isArray(response?.results) ? response.results : [];
      const count = response?.totalCount ?? planData.length;

      setPlans(mapResponseToGridRows(planData, handleViewPlan, canViewPlanRow));
      setTotalCount(Number(count));
    } catch {
      setPlans([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    canViewPlanRow,
    filters,
    isSuperAdmin,
    handleViewPlan,
    listScope,
    page,
    pageSize,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    const handleLocationsFetched = () => {
      setLocationsRevision((revision) => revision + 1);
      loadPlans();
    };

    eventEmitter.on(EVENTS.LOCATIONS_FETCHED, handleLocationsFetched);
    return () => {
      eventEmitter.off(EVENTS.LOCATIONS_FETCHED, handleLocationsFetched);
    };
  }, [loadPlans]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans, refreshSignal]);

  const onPageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const onPageSizeChange = useCallback((nextPageSize) => {
    setPage(1);
    const clampedSize = Math.min(
      Math.max(1, Number(nextPageSize) || REENTRY_PLANS_DEFAULT_PAGE_SIZE),
      MAX_REENTRY_PLANS_PAGE_SIZE,
    );
    setPageSize(clampedSize);
  }, []);

  const onSortChange = useCallback((columnName, direction) => {
    setPage(1);
    setSortBy(mapSortColumnToApi(columnName));
    setSortDirection(direction);
  }, []);

  const onFilterChange = useCallback((nextFilters) => {
    setPage(1);
    setFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  const handleCreateNewPlan = useCallback(() => {
    navigate(CASE_PLAN_CREATE_PATH);
  }, [navigate]);

  // Derived from the mapped rows so the column tracks whatever the per-row rule decided.
  const canViewPlanAction = useMemo(
    () =>
      resolveCanViewPlanColumn({
        canViewAssignedPlans,
        canViewAllPlans: canViewAllPlansDetail,
        listScope,
      }),
    [canViewAllPlansDetail, canViewAssignedPlans, listScope],
  );

  return {
    plans,
    totalCount,
    isLoading,
    pageSize,
    locationFilterOptions,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onFilterChange,
    reloadPlans: loadPlans,
    canViewPlan: canViewAssignedPlans,
    canViewAllPlansDetail,
    canViewPlanAction,
    canCreatePlan,
    handleCreateNewPlan,
  };
};
